import React, { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import { gsap, Draggable } from 'gsap/all';
import { BaseTexture, Texture, AnimatedSprite, Application } from 'pixi.js';
import cls from 'classnames';
import createImageLoader from '@dcode/loader/createImageLoader';
import padStart from '~/utils/number/padStart';
import lerp from '~/utils/number/lerp';
import range from '~/utils/list/range';

gsap.registerPlugin(Draggable);

export const V360 = forwardRef(({
	className,
	classes,
	fps,
	width,
	height,
	loop,
	drag,
	digits,
	startAt,
	stepFrames,
	onLoadStart,
	onLoadError,
	onLoadProgress,
	onLoadComplete,
	onRotate,
	texturesPath,
	totalTextures,
	texturesToLoad:totalFrames,
	texturesExtensionFiles,
	shouldResetPositionOnChange,
	...props
}, ref) => {
	const x = useRef(0);
	const vx = useRef(0);
	const interpolation = useRef(0);
	const isDragging = useRef(false);
	const lastFrame = useRef(0);
	const currentFrame = useRef(0);
	const disposeLoaderTextures = useRef();
	const frames = useRef();
	const app = useRef();
	const rootRef = useRef();
	const wrapperRef = useRef();
	const handleRef = useRef();
	const draggableRefs = useRef();


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// TEXTURES
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	const convertImageToTexture = ({ element:img }) => {
		const baseTexture = new BaseTexture(img);
		const texture = new Texture(baseTexture);
		return texture;
	};

	const loadTextures = (assets, onComplete) => {
		disposeLoaderTextures.current = createImageLoader({
			assets,
			onStart: onLoadStart,
			onError: onLoadError,
			onProgress: onLoadProgress,
			onEmpty: () => console.warn('There\'s no texture to load!'),
			onComplete: (response) => {
				const images = response.list();
				const textures = images.map(convertImageToTexture);
				onComplete(textures);
				onLoadComplete();
			},
		});
	};

	const cancelLoadTextures = () => {
		if (disposeLoaderTextures.current) {
			disposeLoaderTextures.current();
		}
	};


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// ANIMATION / CONTROLS
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	const resetPosition = () => {
		x.current = 0;
		vx.current = 0;
		interpolation.current = 0;
		currentFrame.current = 0;
		lastFrame.current = 0;
	};

	const gotoAndStop = (frame) => {
		if (!frames.current) return false;
		frames.current.gotoAndStop(frame);
		onRotate(frame / totalFrames);
	};


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// DRAG
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	const onDragStart = () => {
		isDragging.current = true;
	};

	const onDrag = (draggableElement) => {
		isDragging.current = true;
		vx.current = draggableElement.deltaX;
	};

	const onDragEnd = () => {
		isDragging.current = false;
	};

	const createDragEvents = () => {
		if (totalFrames <= 1) return;
		draggableRefs.current = Draggable.create(handleRef.current, {
			cursor: 'grab',
			trigger: rootRef.current,
			maxDuration: totalFrames / fps,
			onDragStart() { onDragStart(this); },
			onDrag() { onDrag(this); },
			onDragEnd() { onDragEnd(this); },
		});
	};

	const disposeDragEvents = () => {
		if (draggableRefs.current) {
			draggableRefs.current.forEach((drag) => drag.kill());
		}
	};


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// FRAMES
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	const updateFrame = () => {
		if (!isDragging.current) {
			vx.current *= 0.85;
			if (Math.abs(vx.current) < 10) {
				vx.current *= 0.1;
			}
		}
		x.current += (vx.current / 30);
		interpolation.current = lerp(interpolation.current, x.current, 0.5);
		currentFrame.current = Math.round(interpolation.current) % totalFrames;
		if (currentFrame.current < 0) {
			currentFrame.current = totalFrames + currentFrame.current;
		}
		currentFrame.current = Math.abs(currentFrame.current);
		if (currentFrame.current !== lastFrame.current) {
			lastFrame.current = currentFrame.current;
			gotoAndStop(currentFrame.current);
		}
	};

	const createRenderFrame = () => {
		if (app.current) {
			app.current.ticker.add(updateFrame);
			app.current.ticker.start();
		}
	};

	const cancelRenderFrame = () => {
		if (app.current) {
			app.current.ticker.remove(updateFrame);
			app.current.ticker.stop();
		}
	};

	const attachFrames = (textures) => {
		detachFrames();
		frames.current = new AnimatedSprite(textures);
		frames.current.animationSpeed = 0.35;
		frames.current.loop = false;
		frames.current.gotoAndStop(currentFrame.current);
		createDragEvents();
		createRenderFrame();
		app.current.stage.addChild(frames.current);
	};

	const detachFrames = () => {
		disposeDragEvents();
		cancelRenderFrame();
		if (app.current && frames.current) {
			app.current.stage.removeChild(frames.current);
		}
	};


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// CANVAS
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	const createCanvas = () => {
		app.current = new Application({
			width,
			height,
			autoStart: false,
			transparent: true,
			autoDensity: false,
			resolution: 1,
		});
		wrapperRef.current.appendChild(app.current.view);
		return disposeCanvas;
	};

	const disposeCanvas = () => {
		disposeView();
		if (app.current) {
			app.current.destroy(true, {
				children: true,
				texture: true,
				baseTexture: true,
			});
			app.current = null;
		}
	};


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// View 360
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	const createView = () => {
		const ext = texturesExtensionFiles.charAt(0) === '.' ? texturesExtensionFiles : '.png';
		const assets = range(startAt, totalFrames, stepFrames, false, (num) => (
			`${texturesPath}${padStart(num, digits)}${ext}`
		));
		if (shouldResetPositionOnChange) resetPosition();
		loadTextures(assets, attachFrames);
		return disposeView;
	};

	const disposeView = () => {
		cancelRenderFrame();
		cancelLoadTextures();
	};


	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
	// STARTUP
	// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	useImperativeHandle(ref, () => ({
		root: rootRef.current,
		wrapper: wrapperRef.current,
		handle: handleRef.current,
		resetPosition,
		gotoAndStop,
	}));

	useEffect(createCanvas, []);
	useEffect(createView, [width, height, totalFrames, totalTextures, texturesPath]);
	return (
		<div
			{...props}
			ref={rootRef}
			className={cls(classes.root, className)}>
			<div
				ref={wrapperRef}
				className={cls(classes.wrapper)}>
			</div>
			<div
				ref={handleRef}
				className={cls(classes.handle)}>
			</div>
		</div>
	);
});

export default V360;
